import { useState } from "react"
import Icon from "@/v2-ui/icon"
import LegalBillingBatchRow from "@/v2-backoffice-client/legalBilling/LegalBillingBatchRow"
import LegalBillingTotalsSummary from "@/v2-backoffice-client/legalBilling/LegalBillingTotalsSummary"
import LegalBillingInvoiceActions from "@/v2-backoffice-client/legalBilling/LegalBillingInvoiceActions"
import { tagGroupLabel } from "@/v2-backoffice-client/legalBilling/legalBilling.utils"
import type {
  LegalBillingInvoiceAction,
  LegalBillingTagGroup,
} from "@/v2-backoffice-client/legalBilling/legalBilling.types"

export type LegalBillingTagGroupRowProps = {
  tagGroup: LegalBillingTagGroup
  isPending?: boolean
  onInvoice(action: LegalBillingInvoiceAction): void
}

// One invoicing unit: every batch of this tenant sharing a tag string. Expanded by default
// — this is the level an admin actually invoices at, so its batches are the working view,
// unlike the batch's own document lines.
function LegalBillingTagGroupRow(props: LegalBillingTagGroupRowProps) {
  const { tagGroup, isPending, onInvoice } = props
  const [isOpen, setIsOpen] = useState(true)

  const label = tagGroupLabel(tagGroup.Tag)

  return (
    <div className="legal-billing-tag-group border-t border-border pl-4 py-2">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="flex items-center gap-2 text-sm"
          aria-expanded={isOpen}
          aria-label={`Tag group ${label}`}
          data-feature-code="UI_BackofficeLegalBilling_TagGroup_Toggle"
          onClick={() => setIsOpen((open) => !open)}
        >
          <Icon src={isOpen ? "angle-small-down" : "angle-small-right"} />
          <span className="font-medium">{label}</span>
          <span className="text-xs text-muted-foreground">
            {tagGroup.Batches.length} batches
          </span>
        </button>

        <div className="flex items-center gap-3">
          <LegalBillingTotalsSummary
            totals={tagGroup.Totals}
            invoiced={tagGroup.Invoiced}
          />
          <LegalBillingInvoiceActions
            batches={tagGroup.Batches}
            isPending={isPending}
            onInvoice={onInvoice}
            scopeLabel={label}
          />
        </div>
      </div>

      {isOpen
        ? tagGroup.Batches.map((batch) => (
            <LegalBillingBatchRow
              key={batch.OrderId}
              batch={batch}
              isPending={isPending}
              onInvoice={onInvoice}
            />
          ))
        : null}
    </div>
  )
}

export default LegalBillingTagGroupRow
